import { CommonModule } from '@angular/common';
import { Component, effect, input, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, map, tap } from 'rxjs';
import { MenuCollapseItemComponent } from '../menu-collapse-item/menu-collapse-item.component';
import {
  HoveredLeftMenuDirective,
  MenuOptions,
} from '../../directives/hoveredLeftMenu.directive';
import { NavigationItem } from '../../interfaces/navigationList.interface';
import { PageContainerService } from '../../services/page-container.service';
import { environment } from 'src/environments/environment';
import { NavigationService } from 'src/app/shared/services/navigation.service';

@Component({
  selector: 'z-icon-button',
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    HoveredLeftMenuDirective,
    MenuCollapseItemComponent,
    RouterModule,
  ],
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss'],
})
export class IconButtonComponent {
  navigationItem = input.required<NavigationItem>();

  isActive = signal(false);
  canHover = signal(false);
  menuOptions: MenuOptions = {
    autoClose: false,
    menuWidth: '260px',
  };

  menuExpanded$ = this.pageContainerService.menuExpanded;
  activeSectionRoute$ = this.pageContainerService.activeSectionRoute;

  activeChild!: NavigationItem | null;

  combineExpandedActiveSection$ = combineLatest([
    this.activeSectionRoute$,
    this.menuExpanded$,
  ]).pipe(
    tap(([x, menuExpanded]) => {
      if (x && x.id === this.navigationItem()?.id) {
        this.getRoutePath(x);
        if (menuExpanded) {
          this.canHover.set(false);
        } else {
          this.canHover.set(this.navigationItem().children.length > 0);
        }
        this.isActive.set(true);
      } else {
        this.canHover.set(this.navigationItem().children.length > 0);
        this.isActive.set(false);
        this.activeChild = null;
      }
    }),
    map(([x]) => x),
  );

  constructor(
    readonly pageContainerService: PageContainerService,
    readonly router: Router,
    readonly navigationService: NavigationService,
  ) {
    effect(
      () => {
        this.canHover.update(x => {
          return this.navigationItem().children.length > 0;
        });
      },
      { allowSignalWrites: true },
    );
  }

  onClick() {
    if (this.navigationItem().routeUrl === '') {
      return;
    }
    if (this.navigationItem().isOutSideUrl) {
      // If the routeUrl is a full URL, open it directly
      if (
        this.navigationItem().routeUrl &&
        (this.navigationItem().routeUrl.startsWith('http://') ||
          this.navigationItem().routeUrl.startsWith('https://'))
      ) {
        window.open(this.navigationItem().routeUrl, '_self');
        return;
      }
      // Otherwise, construct the URL as before
      const service =
        this.navigationItem().outsideUrlTarget === 'core'
          ? ''
          : `/${this.navigationItem().outsideUrlTarget}/#`;
      const url =
        environment.localDomain + service + this.navigationItem().routeUrl;
      window.open(url, '_self');
      return;
    }
    this.pageContainerService.activeSectionRoute.next(this.navigationItem());
  }

  getRoutePath(item: NavigationItem | null) {
    if (item) {
      const currentPath = this.pageContainerService
        .getRoutePath()
        .find(x => x.url === this.router.url)?.routePath;

      const index = this.navigationItem().children.findIndex(
        y => y && currentPath && y.id === currentPath[1],
      );
      if (index !== -1) {
        this.activeChild = this.navigationItem().children[index];
      } else {
        return;
      }
    }
  }

  navigateToMicroService(item: NavigationItem) {
    if (!item.isOutSideUrl) {
      return;
    }
    this.navigationService.getAndSetCurrentRoute();
    // If the routeUrl is a full URL, open it directly
    if (
      item.routeUrl &&
      (item.routeUrl.startsWith('http://') ||
        item.routeUrl.startsWith('https://'))
    ) {
      window.open(item.routeUrl, '_self');
      return;
    }
    const service =
      item.outsideUrlTarget === 'core' ? '' : `/${item.outsideUrlTarget}/#`;
    const url = environment.localDomain + service + item.routeUrl;
    window.open(url, '_self');
  }
}
